import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSpring, animated } from 'react-spring';

require('./Dialog.css');

export type Props = {
    id?: string;
    visible: boolean;
    title?: string;
    titleKey?: string;
    children?: any;
    zIndex?: number;
    portalName?: string; // Defaults to "modal-portal" but can be overridden to use a specific portal (See index.html)
};

const paddingTop = {
    paddingTop: '.5em',
};

export const Dialog = ({ visible, id, title, titleKey, portalName, children }: Props) => {
    const { t } = useTranslation();

    const [ mounted, setMounted ] = useState(visible);
    const [ backgroundStyle, backgroundStyleAnim ] = useSpring(() => ({
        from: { opacity: 0 },
    }));
    const [ dialogStyle, dialogStyleAnim ] = useSpring(() => ({
        config: { mass: 1, tension: 400, friction: 30 },
        from: { opacity: 0, transform: 'scale(0.01)' },
    }));

    useEffect(() => {
        // When visible is true, mount the portal immediately
        if (visible) {
            setMounted(true);
        }

        // Animate the new visible state
        backgroundStyleAnim.start({
            to: { opacity: visible ? .4 : 0 },
        });
        dialogStyleAnim.start({
            to: { opacity: visible ? 1 : 0, transform: visible ? 'scale(1)' : 'scale(0.01)' },
            onRest: () => !visible && setMounted(false), // Unmount when hide animation ends
        });

        // Cancel animations if we happen to leave (i.e. parent unmounts) before they finish
        return () => {
            backgroundStyleAnim.stop();
            dialogStyleAnim.stop();
        };
    }, [ visible, setMounted, backgroundStyleAnim, dialogStyleAnim ]);

    // We saw a very strange occurrence through Storybook where one story
    // (AssignDeviceToElevatorDialog.stories.tsx) was failing to find the
    // modal-portal div, which was clearly in the stories file... and all
    // other dialog stories were working. On the assumption that this was
    // a timing issue, we now query for the modal-portal div once mounted
    // changes. This seems to fix the problem.
    const [ modalPortal, setModalPortal ] = useState<HTMLElement | null>(null);
    useEffect(() => {
        const div = document.getElementById(portalName || 'modal-portal');
        if (!div) {
            throw new Error(`Could not find modal portal named "${portalName || 'modal-portal'}"`);
        }
        setModalPortal(div);
    }, [ mounted, portalName ]);

    return !mounted || !modalPortal ? null : ReactDOM.createPortal(
        <div className="dialog-container">
            <animated.div
                style={ backgroundStyle }
                className="dialog-background"
            >
                &nbsp;
            </animated.div>
            <animated.div
                style={ dialogStyle }
                className="dialog-host"
            >
                <div
                    id={ id }
                    className="dialog"
                >
                    { (title || titleKey) &&
                        <h2 className="ui dividing header">
                            { title || t(titleKey!) }
                        </h2>
                    }
                    <div style={ paddingTop }>
                        { children }
                    </div>
                </div>
            </animated.div>
        </div>,
        modalPortal
    );
};
